import { auth } from '../firebase';

const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5010/api';

let csrfToken = null;
let csrfTokenScope = 'anonymous';

const inFlightRequests = new Map();
const requestCache = new Map();
const CACHE_TTL = 60000;

const getAuthScope = (options = {}) => {
  if (options.headers?.Authorization) {
    return 'auth-header';
  }

  return auth.currentUser?.uid || 'anonymous';
};

export const fetchCsrfToken = async () => {
  try {
    const response = await fetch(`${apiUrl}/antiforgery/token`, {
      credentials: 'include', // Include cookies for session binding
    });
    
    if (response.ok) {
      const data = await response.json();
      csrfToken = data.token;
      csrfTokenScope = auth.currentUser?.uid || 'anonymous';
      return csrfToken;
    }

    csrfToken = null;
  } catch {
    // CSRF token fetch failed - will retry on next request
    csrfToken = null;
  }

  return null;
};

const apiRequest = async (endpoint, options = {}, retryCount = 0, csrfRetryCount = 0) => {
  const MAX_RETRIES = 3;
  const BASE_RETRY_DELAY = 1000;
  const method = (options.method || 'GET').toUpperCase();
  const authScope = getAuthScope(options);
  
  // REQUEST DEDUPLICATION: For GET requests, check if an identical request is already in flight
  const cacheKey = `${method}:${endpoint}:${authScope}`;
  if (method === 'GET' && inFlightRequests.has(cacheKey)) {
    // Return the existing request promise
    return inFlightRequests.get(cacheKey);
  }

  // CACHING: For GET requests, check if cached response is still valid
  if (method === 'GET') {
    const cached = requestCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }
  }

  // Create a new request promise
  const requestPromise = (async () => {
    try {
      // Get Firebase ID token if user is authenticated
      let token = null;
      if (auth.currentUser) {
        try {
          token = await auth.currentUser.getIdToken();
        } catch {
          // Silent fail - authentication handled by backend
        }
      }
      
      const headers = {
        ...options.headers,
        ...(!options.headers?.Authorization && token && { Authorization: `Bearer ${token}` }),
      };

      const isFormDataBody = typeof FormData !== 'undefined' && options.body instanceof FormData;
      if (options.body && !isFormDataBody && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }

      if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method) && csrfToken) {
        headers['X-CSRF-TOKEN'] = csrfToken;
      }

      // Ensure CSRF token is available before state-changing requests.
      const requestCsrfScope = auth.currentUser?.uid || 'anonymous';
      const csrfScopeChanged = csrfTokenScope !== requestCsrfScope;
      const isStateChangingMethod = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);

      if (isStateChangingMethod && (!csrfToken || csrfScopeChanged)) {
        await fetchCsrfToken();
        if (!csrfToken) {
          throw new Error('Unable to acquire CSRF token. Please refresh the page and try again.');
        }

        headers['X-CSRF-TOKEN'] = csrfToken;
      }

      const fullUrl = `${apiUrl}${endpoint}`;

      const res = await fetch(fullUrl, { 
        ...options, 
        headers,
        credentials: 'include'
      });

      // Handle HTTP error responses (4xx, 5xx status codes)
      if (!res.ok) {
        let errText = await res.text();
        let err;
        try {
          // Attempt to parse error response as JSON
          err = JSON.parse(errText);
        } catch {
          // If not JSON, treat as plain text error
          err = { error: errText || 'Request failed' };
        }

        // Include status code in error message for proper error handling
        const errorMessage = err.error || res.statusText || 'Request failed';

        // RATE LIMIT HANDLING: Implement exponential backoff for 429 errors
        if (res.status === 429 && retryCount < MAX_RETRIES) {
          const backoffDelay = BASE_RETRY_DELAY * Math.pow(2, retryCount);
          const jitter = Math.random() * 1000;
          const totalDelay = backoffDelay + jitter;
          
          console.warn(
            `Rate limited (429). Retrying after ${Math.round(totalDelay)}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`
          );
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, totalDelay));
          
          // Recursive retry with incremented counter
          return apiRequest(endpoint, options, retryCount + 1, csrfRetryCount);
        }

        // CSRF RECOVERY: refresh token and retry once for stale/missing token errors.
        const looksLikeCsrfError = typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('csrf');
        if (isStateChangingMethod && res.status === 400 && looksLikeCsrfError && csrfRetryCount < 1) {
          csrfToken = null;
          await fetchCsrfToken();

          if (csrfToken) {
            return apiRequest(endpoint, options, retryCount, csrfRetryCount + 1);
          }
        }
        throw new Error(`${res.status} ${errorMessage}`);
      }

      if (res.status === 204) return null;
      
      const ct = res.headers.get('content-type') || '';
      const responseData = ct.includes('application/json') ? await res.json() : await res.text();
      
      // CACHING: Store GET request responses in cache
      if (method === 'GET') {
        requestCache.set(cacheKey, {
          data: responseData,
          timestamp: Date.now()
        });
      }
      
      return responseData;
    } finally {
      // Remove from in-flight requests map
      if (method === 'GET') {
        inFlightRequests.delete(cacheKey);
      }
    }
  })();

  // Store promise for GET requests to enable deduplication
  if (method === 'GET') {
    inFlightRequests.set(cacheKey, requestPromise);
  }

  return requestPromise;
};

export default apiRequest;

// Invalidate request caches when auth identity changes to prevent cross-session stale reads.
auth.onAuthStateChanged(() => {
  inFlightRequests.clear();
  requestCache.clear();
  csrfToken = null;
  csrfTokenScope = auth.currentUser?.uid || 'anonymous';
});
